import 'reflect-metadata';
import React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { RouteSelector } from './components/route-selector/route-selector';
import { createTheme, CssBaseline, ThemeProvider } from '@mui/material';
import axios from 'axios';
// from https://medium.com/geekculture/strong-typed-i18n-in-react-c43281de720c
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import hr from './translations/hr.json';
import en from './translations/en.json';
import { CompanyBreadcrumbProvider } from './breadcrumb-providers/company.breadcrumb-provider';
import { ProjectBreadcrumbProvider } from './breadcrumb-providers/project.breadcrumb-provider';
import { CompanyBranchBreadcrumbProvider } from './breadcrumb-providers/company-branch.breadcrumb-provider';
import { registerBreadcrumbDataFunction } from '@lib/ui-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ProjectBranchBreadcrumbProvider } from './breadcrumb-providers/project-branch.breadcrumb-provider';
import { CommuneBreadcrumbProvider } from './breadcrumb-providers/commune.breadcrumb-provider';

const resources = {
  hr,
  en,
};

i18n
  .use(initReactI18next)
  .use(LanguageDetector)
  .init({
    resources,
    ns: ['common', 'error'],
    defaultNS: 'common',
    fallbackLng: 'hr',
    nonExplicitSupportedLngs: true,
    supportedLngs: ['hr', 'en'],
    lng: 'hr',
  });

const theme = createTheme({
  typography: {
    fontFamily: ['Montserrat', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
    button: {
      textTransform: 'none',
    },
  },
  palette: {
    primary: {
      main: '#183362',
    },
    secondary: {
      main: '#F2C94C',
      contrastText: '#183362',
    },
    info: {
      main: '#f2f2f2',
      contrastText: '#183362',
    },
  },
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#F2C94C',
          },
          backgroundColor: '#f2f2f2',
          border: '1px solid #e8e8e8',
          borderRadius: '0.5rem 0.5rem 0 0',
          marginRight: '0.2rem',
          minHeight: '1rem',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          marginBottom: '-0.3rem',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginTop: '0px',
          whiteSpace: 'pre-line',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        container: {
          alignItems: 'flex-start',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        filledInfo: {
          backgroundColor: '#2196F3',
          // The palette's info.main is #f2f2f2 with a navy contrastText, which is what a filled Alert takes its
          // text colour from - so overriding only the background left navy text on a blue field. The warning
          // override below keeps the computed dark text on purpose: its background is a light yellow.
          color: '#ffffff',
        },
        filledWarning: {
          backgroundColor: '#ECBC49',
        },
      },
    },
  },
});

const queryClient = new QueryClient();

axios.defaults.baseURL = import.meta.env.VITE_API_URL;
axios.defaults.withCredentials = true;
axios.defaults.paramsSerializer = {
  serialize: (params) => {
    const keys = Object.keys(params);
    let options = '';

    keys.forEach((key) => {
      const isParamTypeObject = typeof params[key] === 'object';
      const isParamTypeArray = isParamTypeObject && params[key].length >= 0;

      if (!isParamTypeObject) {
        if (params[key] !== undefined) {
          options += `${key}=${params[key]}&`;
        }
      }

      if (isParamTypeObject && isParamTypeArray) {
        params[key].forEach((element: any) => {
          if (element !== undefined) {
            options += `${key}=${element}&`;
          }
        });
      }
    });

    return options ? options.slice(0, -1) : options;
  },
};

registerBreadcrumbDataFunction('Breadcrumb.companies', CompanyBreadcrumbProvider);
registerBreadcrumbDataFunction('Breadcrumb.companies.$', CompanyBranchBreadcrumbProvider);
registerBreadcrumbDataFunction('Breadcrumb.projects', ProjectBreadcrumbProvider);
registerBreadcrumbDataFunction('Breadcrumb.projects.$', ProjectBranchBreadcrumbProvider);
registerBreadcrumbDataFunction('Breadcrumb.communes', CommuneBreadcrumbProvider);
registerBreadcrumbDataFunction('Breadcrumb.pickups', ProjectBranchBreadcrumbProvider);
registerBreadcrumbDataFunction('Breadcrumb.branches', ProjectBranchBreadcrumbProvider);

const container = document.getElementById('root');
if (!container) throw new Error('Failed to find the root element');
const root = ReactDOMClient.createRoot(container);

root.render(
  <>
    <CssBaseline />
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <RouteSelector />
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  </>,
);
